import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

// Get template type and enabled routes from build-time env
const templateType = import.meta.env.VITE_TEMPLATE_TYPE || 'multi-page';
const enabledRoutesStr = import.meta.env.VITE_ENABLED_ROUTES || '';
const enabledRoutes = enabledRoutesStr.split(',').filter(Boolean);

// Helper to conditionally lazy load components
const conditionalImport = (
  routeName: string,
  importFn: () => Promise<{ default: ComponentType<any> }>
): LazyExoticComponent<ComponentType<any>> | null => {
  if (enabledRoutes.includes(routeName) || enabledRoutes.length === 0) {
    return lazy(importFn);
  }
  return null;
};

// Always available routes
export const HomePage = lazy(() => import('../pages/HomePage'));
export const SinglePageStorefront = lazy(() => import('../pages/SinglePageStorefront'));

// Conditionally loaded based on template
export const ProductsPage = conditionalImport('products', () => import('../pages/ProductsPage'));
export const ProductDetailPage = conditionalImport('product-detail', () => import('../pages/ProductDetailPage'));
export const EventsPage = conditionalImport('events', () => import('../pages/EventsPage'));
export const EventBookingPage = conditionalImport('events', () => import('../pages/EventBookingPage'));
export const CartPage = conditionalImport('cart', () => import('../pages/CartPage'));
// Force enable critical pages for multi-page funnels
export const CheckoutPage = lazy(() => import('../pages/CheckoutPage'));
export const ContactPage = lazy(() => import('../pages/ContactPage'));
export const CheckoutSuccess = lazy(() => import('../pages/CheckoutSuccess'));
export const CheckoutFailure = lazy(() => import('../pages/CheckoutFailure'));
export const Orders = conditionalImport('orders', () => import('../pages/Orders'));
export const OrderDetails = conditionalImport('orders', () =>
  import('../pages/OrderDetails').then(m => ({ default: m.OrderDetails }))
);
export const OrderConfirmationPage = lazy(() => import('../pages/OrderConfirmationPage'));
export const BookingConfirmationPage = conditionalImport('events', () => import('../pages/BookingConfirmationPage'));
export const DynamicPage = conditionalImport('dynamic-pages', () => import('../pages/DynamicPage'));
export const PagesRedirect = lazy(() => import('../pages/PagesRedirect'));
export const StorefrontPage = conditionalImport('storefront', () => import('../pages/StorefrontPage'));
// Force enable FunnelPage to ensure it's always available
export const FunnelPage = lazy(() => import('../pages/FunnelPage'));
export const ManageBookingPage = lazy(() => import('../pages/ManageBookingPage'));
export const MyBookingsPage = lazy(() => import('../pages/MyBookingsPage'));

// Route configuration interface
export interface RouteConfig {
  path: string;
  element: LazyExoticComponent<ComponentType<any>> | null;
}

// Route definitions - filter out disabled routes at build time.
// Customer storefront is guest-only — /orders and /orders/:id use the page's
// own email-based lookup, no auth gate.
export const routes: RouteConfig[] = [
  { path: '/', element: HomePage },
  { path: '/products', element: ProductsPage },
  { path: '/products/:handle', element: ProductDetailPage },
  { path: '/cart', element: CartPage },
  { path: '/checkout', element: CheckoutPage },
  { path: '/contact', element: ContactPage },
  { path: '/checkout/success', element: CheckoutSuccess },
  { path: '/checkout/failure', element: CheckoutFailure },
  { path: '/events', element: EventsPage },
  { path: '/events/:id/book', element: EventBookingPage },
  { path: '/events/booking/confirmation', element: BookingConfirmationPage },
  { path: '/orders', element: Orders },
  { path: '/orders/:id', element: OrderDetails },
  // Param MUST be :orderId (not :id) — OrderConfirmationPage reads
  // useParams().orderId and only fetches when it's truthy. Naming it :id left
  // orderId undefined, so this route (used by the admin builder preview at
  // /orders/confirmation/preview) never loaded and hung on the spinner.
  { path: '/orders/confirmation/:orderId', element: OrderConfirmationPage },
  // Match the path emitted by getSuccessRoute() for non-funnel storefronts;
  // the catch-all /:slug → DynamicPage was swallowing this otherwise.
  { path: '/order-confirmation/:orderId', element: OrderConfirmationPage },
  { path: '/order-success/:orderId', element: OrderConfirmationPage },
  { path: '/storefront', element: StorefrontPage },
  { path: '/f/:slug', element: FunnelPage },
  { path: '/f/:slug/checkout', element: CheckoutPage },
  { path: '/f/:slug/order-confirmation/:orderId', element: OrderConfirmationPage },
  { path: '/f/:slug/order-success/:orderId', element: OrderConfirmationPage },
  { path: '/manage-booking/:id', element: ManageBookingPage },
  { path: '/my-bookings', element: MyBookingsPage },
  // Legacy /pages/:slug → 301-style client redirect to /:slug. Kept so old
  // bookmarks, indexed URLs, and any stray hardcoded links keep working.
  { path: '/pages/:slug', element: PagesRedirect },
  // Catch-all for root level dynamic pages (must be last)
  { path: '/:slug', element: DynamicPage },
].filter(route => route.element !== null); // Filter out disabled routes at build time
